import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Package, Search, Plus, Edit2, Trash2, Loader2, FileText, Euro,
  CheckCircle, XCircle, RefreshCw, MoreHorizontal, Power, PowerOff
} from 'lucide-react';
import { getTypeIcon } from './catalogShared';

function ItemSpecsCell({ item }) {
  if (item.type_code === 'MODULI' && item.technical_specs?.pmax_w) {
    return <span className="text-sm text-muted-foreground">{item.technical_specs.pmax_w}W</span>;
  }
  if (item.type_code === 'INVERTER' && item.technical_specs?.potenza_nominale_ac_kw) {
    return <span className="text-sm text-muted-foreground">{item.technical_specs.potenza_nominale_ac_kw}kW</span>;
  }
  if (item.type_code === 'STORAGE' && item.technical_specs?.capacita_nominale_kwh) {
    return <span className="text-sm text-muted-foreground">{item.technical_specs.capacita_nominale_kwh}kWh</span>;
  }
  return null;
}

function ItemRow({ item, typeInfo, onViewDetail, onEdit, onAction }) {
  return (
    <TableRow data-testid={`item-row-${item.id}`}>
      <TableCell className="font-mono text-sm">{item.item_code}</TableCell>
      <TableCell>
        <Badge variant="outline" className="flex items-center gap-1 w-fit">
          {getTypeIcon(item.type_code)}
          {typeInfo?.name || item.type_code}
        </Badge>
      </TableCell>
      <TableCell className="font-medium">{item.brand}</TableCell>
      <TableCell>{item.model}</TableCell>
      <TableCell><ItemSpecsCell item={item} /></TableCell>
      <TableCell className="text-right">
        {item.prezzo_listino ? (
          <span className="flex items-center justify-end gap-1">
            <Euro className="h-3 w-3" />
            {item.prezzo_listino.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell>
        {item.is_active ? (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Attivo
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">
            <XCircle className="h-3 w-3 mr-1" />
            Inattivo
          </Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => onViewDetail(item)} data-testid={`view-item-${item.id}`}>
            <FileText className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => onEdit(item)} data-testid={`edit-item-${item.id}`}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={`actions-menu-${item.id}`}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {item.is_active ? (
                <DropdownMenuItem onClick={() => onAction(item, 'deactivate')} className="text-amber-600">
                  <PowerOff className="h-4 w-4 mr-2" />
                  Disattiva
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onAction(item, 'activate')} className="text-emerald-600">
                  <Power className="h-4 w-4 mr-2" />
                  Riattiva
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onAction(item, 'delete')} className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Elimina definitivamente
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function CatalogItemsTable({
  items, itemsLoading, selectedType, getTypeInfo,
  searchQuery, setSearchQuery,
  onRefresh, onCreate, onViewDetail, onEdit, onAction
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            {selectedType ? (
              <>
                {getTypeIcon(selectedType)}
                {getTypeInfo(selectedType)?.name || selectedType}
              </>
            ) : (
              'Tutti gli articoli'
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca marca, modello..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" size="icon" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {itemsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Nessun articolo trovato</p>
            <Button variant="outline" className="mt-4" onClick={onCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Aggiungi il primo articolo
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Codice</TableHead>
                <TableHead>Tipologia</TableHead>
                <TableHead>Marca</TableHead>
                <TableHead>Modello</TableHead>
                <TableHead>Specifiche</TableHead>
                <TableHead className="text-right">Prezzo</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(item => (
                <ItemRow
                  key={item.id}
                  item={item}
                  typeInfo={getTypeInfo(item.type_code)}
                  onViewDetail={onViewDetail}
                  onEdit={onEdit}
                  onAction={onAction}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
